import { useEffect } from "react";

import { EventType } from "@azure/msal-browser";
import { ohQueryClient } from "@shared/libs/react-query/query-client";
import { CollaboratorQueryKeys } from "@shared/libs/react-query/query-keys.constants";
import { msalInstance } from "hassibot/services_v2/login/msal";
import { uuidv4 } from "hassibot/util/uuid";
import {
  BROADCAST_CHANNEL_AUTH_SYNC,
  emitOnBroadcastChannel,
  listenOnBroadcastChannel,
} from "startup/custom-broadcast-channel";
import { assertNever } from "types";

const authSyncReason = {
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGOUT_SUCCESS: "LOGOUT_SUCCESS",
  ACCOUNT_ADDED: "ACCOUNT_ADDED",
  ACCOUNT_REMOVED: "ACCOUNT_REMOVED",
} as const;

export type AuthSyncMessage = {
  type:
    | typeof authSyncReason.LOGIN_SUCCESS
    | typeof authSyncReason.LOGOUT_SUCCESS
    | typeof authSyncReason.ACCOUNT_ADDED
    | typeof authSyncReason.ACCOUNT_REMOVED;
  senderId: string;
};

// Unique ID for this tab instance to identify our own messages
const TAB_ID = uuidv4();

/**
 * Listen for auth sync messages from other tabs and trigger page reload.
 * This ensures the current tab updates its auth state when another tab logs in/out.
 *
 * @returns Cleanup function to stop listening
 */
const listenForAuthChanges = (): (() => void) => {
  return listenOnBroadcastChannel(BROADCAST_CHANNEL_AUTH_SYNC, (event: MessageEvent) => {
    const message = event.data as AuthSyncMessage;

    // Ignore messages from this tab - BroadcastChannel sends to all tabs including sender
    if (message.senderId === TAB_ID) {
      console.log("[AuthSync] Ignoring own message:", message.type);
      return;
    }

    console.log("[AuthSync] Received message from another tab:", message);

    switch (message.type) {
      case authSyncReason.LOGIN_SUCCESS:
      case authSyncReason.ACCOUNT_ADDED:
        console.log("[AuthSync] User logged in on another tab, reloading...");
        window.location.reload();
        break;

      case authSyncReason.LOGOUT_SUCCESS:
      case authSyncReason.ACCOUNT_REMOVED:
        console.log("[AuthSync] User logged out on another tab, reloading...");
        window.location.reload();
        break;

      default:
        assertNever(message.type);
    }
  });
};

/**
 * Emit auth change events to other tabs when auth state changes in this tab.
 *
 * @returns Cleanup function to remove event callback
 */
const emitForAuthChange = (): (() => void) => {
  const callbackId = msalInstance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      // Invalidate user query to force refresh after login
      ohQueryClient.invalidateQueries([CollaboratorQueryKeys.ME]);

      // Notify other tabs about login
      console.log("[AuthSync] Login detected, notifying other tabs");
      emitOnBroadcastChannel(BROADCAST_CHANNEL_AUTH_SYNC, {
        type: authSyncReason.LOGIN_SUCCESS,
        senderId: TAB_ID,
      });
    }

    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      // Clear all queries on logout
      ohQueryClient.clear();

      // Notify other tabs about logout
      console.log("[AuthSync] Logout detected, notifying other tabs");
      emitOnBroadcastChannel(BROADCAST_CHANNEL_AUTH_SYNC, {
        type: authSyncReason.LOGOUT_SUCCESS,
        senderId: TAB_ID,
      });
    }

    if (event.eventType === EventType.ACCOUNT_ADDED) {
      console.log("[AuthSync] Account added, notifying other tabs");
      emitOnBroadcastChannel(BROADCAST_CHANNEL_AUTH_SYNC, {
        type: authSyncReason.ACCOUNT_ADDED,
        senderId: TAB_ID,
      });
    }

    if (event.eventType === EventType.ACCOUNT_REMOVED) {
      console.log("[AuthSync] Account removed, notifying other tabs");
      emitOnBroadcastChannel(BROADCAST_CHANNEL_AUTH_SYNC, {
        type: authSyncReason.ACCOUNT_REMOVED,
        senderId: TAB_ID,
      });
    }
  });

  const cleanup = (): void => {
    if (callbackId) {
      msalInstance.removeEventCallback(callbackId);
    }
  };
  return cleanup;
};

/**
 * Hook to synchronize authentication state across multiple tabs.
 * Listens for auth changes in other tabs and emits auth changes from this tab.
 */
export const useAuthSync = (): void => {
  useEffect(() => {
    const cleanupEmitForAuthChange = emitForAuthChange();
    const cleanupListenForAuthChanges = listenForAuthChanges();

    const cleanup = (): void => {
      cleanupEmitForAuthChange();
      cleanupListenForAuthChanges();
    };

    return cleanup;
  }, []);
};
